import React from "react";
import {
  RepeatOutlined,
  AddRounded,
  CalendarMonthOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../../shared/hooks/useTranslation";

/**
 * 240px recurring/PM schedules panel — slides out when the Recurring pill is active.
 *
 * The WorkOrder list model doesn't include `recurrenceRule` (only WorkOrderDetail does),
 * and there's no dedicated PM schedule API yet. So this panel shows a placeholder with
 * the approved visual design, ready to be connected when PM schedule CRUD is implemented.
 */
const CalendarRecurringPanel: React.FC = () => {
  const { translate } = useTranslation("operations");

  // Placeholder PM schedule cards (static)
  const placeholders = [
    {
      name: "Weekly Safety Inspection",
      frequency: "Weekly",
      time: "09:00",
      scope: "All CNC machines",
      nextDate: "Apr 1",
      color: "#3B82F6",
    },
    {
      name: "Monthly Lubrication",
      frequency: "Monthly",
      time: "07:30",
      scope: "Lathe department",
      nextDate: "Apr 15",
      color: "#2D8C4E",
    },
    {
      name: "Bi-Weekly Filter Check",
      frequency: "Bi-weekly",
      time: "14:00",
      scope: "Coolant systems",
      nextDate: "Apr 7",
      color: "#8C6BD4",
    },
  ];

  return (
    <div
      className="w-[240px] flex-shrink-0 border-r border-mid-border bg-white flex flex-col min-h-0 overflow-hidden"
      style={{ animation: "fadeSlideRight 0.2s ease both" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-mid-border flex-shrink-0">
        <span
          className="text-sm font-semibold text-mid-charcoal"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          PM Schedules
        </span>
      </div>

      {/* Coming soon notice */}
      <div className="px-3 py-2 bg-[#F7F5F0] border-b border-mid-border flex-shrink-0">
        <span
          className="text-[10px] text-mid-muted"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Preview — schedule management coming soon
        </span>
      </div>

      {/* Scrollable placeholder card list */}
      <div
        className="flex-1 overflow-y-auto px-2 py-2 flex flex-col gap-2"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C1C7CD transparent",
        }}
      >
        {placeholders.map((item, idx) => (
          <div
            key={idx}
            className="flex rounded-lg border border-mid-border overflow-hidden opacity-75 flex-shrink-0"
          >
            {/* Type color stripe */}
            <div
              className="w-[3px] flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />

            {/* Card content */}
            <div className="flex-1 flex flex-col gap-1.5 px-2.5 py-2 min-w-0">
              {/* Schedule name */}
              <span
                className="text-[12px] font-semibold text-mid-charcoal leading-4 line-clamp-1"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {item.name}
              </span>

              {/* Frequency badge + time */}
              <div className="flex items-center gap-2">
                <span
                  className="text-[9px] font-semibold px-1.5 py-px rounded bg-[#F0F2F7] text-mid-muted"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {item.frequency}
                </span>
                <div className="flex items-center gap-0.5">
                  <ScheduleOutlined sx={{ fontSize: 10, color: "#8C9099" }} />
                  <span
                    className="text-[10px] text-mid-muted"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {item.time}
                  </span>
                </div>
              </div>

              {/* Machine scope */}
              <span
                className="text-[10px] text-mid-muted truncate"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {item.scope}
              </span>

              {/* Next occurrence */}
              <div className="flex items-center gap-1">
                <CalendarMonthOutlined sx={{ fontSize: 10, color: "#8C9099" }} />
                <span
                  className="text-[10px] text-mid-muted"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  Next: {item.nextDate}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* + New PM Schedule button (disabled placeholder) */}
      <div className="flex-shrink-0 px-2 pb-2 pt-1 border-t border-mid-border">
        <button
          disabled
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-mid-muted bg-[#F7F5F0] opacity-50 cursor-not-allowed"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          <AddRounded sx={{ fontSize: 16 }} />
          New PM Schedule
        </button>
      </div>

      {/* Slide-in animation keyframe */}
      <style>{`
        @keyframes fadeSlideRight {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default CalendarRecurringPanel;
